import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  useTheme,
  Card,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import Header from "../../../components/Header";
import DynamicForm from "../../../data/Axios/DynamicForm";
import Swal from "sweetalert2";
import {
  allReportMinorClassifications,
  allReportsMajorClassifications,
  allReportTypes,
} from "../../../data/Axios/queries";
import {
  setupManagementUrl,
  reportManagementUrl,
  emailService,
  reportsManagementUrl,
  companyManagementUrl,
  userManagementUrl,
} from "../../../config";
import { generateReport } from "../../../data/DynamicTable/DynamicFunctions";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { allBranches, allUsers , allCompanyBranchesView} from "../../../data/Axios/queries";

const Filter = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ✅ Read token once — determines if user is locked to a branch
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const tokenBranchId = parseInt(decodedToken?.BranchId);
  const isBranchLocked = !isNaN(tokenBranchId) && tokenBranchId > 0;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [major, setMajor] = useState([]);
  const [minor, setMinor] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportName, setReportName] = useState([]);
  const [filteredMinorOptions, setFilteredMinorOptions] = useState([]);
  const [filteredReportTypesOptions, setFilteredReportTypesOptions] = useState(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [reportTypes, setReportType] = useState([]);
  const [selectedReportTypeID, setSelectedReportTypeID] = useState(null);

  useEffect(() => {
    async function fetchAllData() {
      try {
        const Response = await graphqlQuery(
          allReportMinorClassifications,
          reportsManagementUrl.uri
        );
        if (Response) setData(Response);

        const MajorResponse = await graphqlQuery(
          allReportsMajorClassifications,
          reportsManagementUrl.uri
        );
        if (MajorResponse) setMajor(MajorResponse);

        const MinorResponse = await graphqlQuery(
          allReportMinorClassifications,
          reportsManagementUrl.uri
        );
        if (MinorResponse) setMinor(MinorResponse);

        const ReportType = await graphqlQuery(
          allReportTypes,
          reportsManagementUrl.uri
        );
        if (ReportType) setReportType(ReportType);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }

        const BranchResponse = await graphqlQuery(
          allCompanyBranchesView,
          companyManagementUrl.uri
        );
        if (BranchResponse) setBranches(BranchResponse);
      

      const UserResponse = await graphqlQuery(allUsers, userManagementUrl.uri);
      if (UserResponse) setUsers(UserResponse);
    }

    fetchAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
 const lockedBranchName =
  isBranchLocked
      ? `${branches.find((b) => b.id === parseInt(tokenBranchId)) ?.branchName }`
      : "Unknown";


  const majorOptions = major
    ? major.map((item) => ({
        parent_key: item.id,
        value: item.id,
        label: item.majorClassification,
      }))
    : [];

  // ✅ Branch options:
  //    - When locked: only the user's own branch (no "All Branches" option)
  //    - When free:   "All Branches" + every fetched branch
  const branchOptions = isBranchLocked
    ? [
        {
          parent_key: tokenBranchId,
          value: tokenBranchId,
          label: lockedBranchName,
        },
      ]
    : branches
    ? [
        { parent_key: "", value: "", label: "All Branches" },
        ...branches.map((item) => ({
          parent_key: item.id,
          value: item.id,
          label: item.branchName,
        })),
      ]
    : [];

  const userOptions = users
    ? [
        { parent_key: "", value: "", label: "All Users" },
        ...users.map((item) => ({
          parent_key: item.id,
          value: item.id,
          label: item.username,
        })),
      ]
    : [];

  const ReportNames = reportName
    ? reportName.map((item) => ({
        parent_key: item.id,
        value: item.reportName,
        label: item.reportName,
      }))
    : [];

  const selectedReportTypeIdNum = Number(selectedReportTypeID);

  const fields = [
    {
      name: "majorClassificationID",
      label: "Major Classification",
      type: "select",
      options: majorOptions,
      required: true,
      parent: "main",
      gridColumn: "span 1",
    },
    {
      name: "minorClassificationID",
      label: "Minor Classification",
      type: "select",
      options: filteredMinorOptions,
      required: true,
      gridColumn: "span 1",
      parent: "main",
    },
    {
      name: "reportTypeID",
      label: "Report Type",
      type: "select",
      options: filteredReportTypesOptions,
      required: true,
      gridColumn: "span 1",
      parent: "main",
    },
    ...(selectedReportTypeIdNum !== 1 && selectedReportTypeIdNum !== 2
      ? [
          {
            name: "StartDate",
            label: "Start Date",
            type: "date",
            gridColumn: "span 1",
          },
          {
            name: "EndDate",
            label: "End Date",
            type: "date",
            gridColumn: "span 1",
          },
          // ✅ Branch field:
          //    - When locked: read-only display (disabled select with single option)
          //    - When free:   normal select with all branches
          {
            name: "branchID",
            label: isBranchLocked ? `Branch (${lockedBranchName})` : "Branch",
            type: "select",
            options: branchOptions,
            required: false,
            gridColumn: "span 1",
            // ✅ Disable the field so the user cannot change it
            disabled: isBranchLocked,
          },
          ...(selectedReportTypeIdNum === 1019
            ? [
                {
                  name: "userID",
                  label: "User",
                  type: "select",
                  options: userOptions,
                  required: false,
                  gridColumn: "span 1",
                  parent: "main",
                },
              ]
            : []),
        ]
      : []),
  ];

  // ✅ Default branchID to locked branch when applicable
  const initialValues = {
    majorClassificationID: props.data ? props.data.majorClassificationID : "",
    minorClassificationID: props.data ? props.data.minorClassificationID : "",
    reportName: props.data ? props.data.reportName : "",
    StartDate: dayjs().subtract(7, "days").format("YYYY-MM-DD"),
    EndDate: dayjs().format("YYYY-MM-DD"),
    // ✅ Pre-fill with locked branch ID; otherwise use prop or empty
    branchID: isBranchLocked
      ? tokenBranchId
      : props.data
      ? props.data.branchID
      : "",
  };

  const onFieldChange = async (field, value) => {
    let updatedOptions = {};

    if (field.name === "majorClassificationID") {
      const filteredMinorOpts = minor
        .filter((item) => Number(item.majorClassificationID) === Number(value))
        .map((item) => ({
          parent_key: item.majorClassificationID,
          value: item.id,
          label: item.minorClassification,
        }));

      setFilteredReportTypesOptions([]);
      updatedOptions = { minorClassificationID: filteredMinorOpts };
      setFilteredMinorOptions(filteredMinorOpts);
    }

    if (field.name === "branchID") {
      // ✅ If branch is locked, ignore any change attempt (field is disabled anyway)
      if (isBranchLocked) return {};

      if (value === "ALL") {
        const allBranchIDs = branches.map((b) => b.id);
        return { branchID: allBranchIDs };
      }
    }

    if (field.name === "minorClassificationID") {
      const filteredReportTypesOpts = reportTypes
        .filter((item) => Number(item.minorClassificationID) === Number(value))
        .map((item) => ({
          parent_key: item.minorClassificationID,
          value: item.id,
          label: item.reportName,
        }));

      updatedOptions = {
        ...updatedOptions,
        reportTypeID: filteredReportTypesOpts,
      };
      setFilteredReportTypesOptions(filteredReportTypesOpts);
    }

    if (field.name === "reportTypeID") {
      setSelectedReportTypeID(
        value == null || value === "" ? null : Number(value)
      );
    }

    return updatedOptions;
  };
 
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { reportTypeID, StartDate, EndDate, userID } = values;

      const normalizedReportTypeId =
        reportTypeID === "" || reportTypeID == null
          ? null
          : Number(reportTypeID);

      // ✅ Always use locked branch ID when applicable, ignoring form value
      const branchID = isBranchLocked
        ? tokenBranchId
        : values.branchID === "" || values.branchID == null
        ? null
        : Number(values.branchID);
        

      const selectedReport = reportTypes.find(
        (item) => Number(item.id) === normalizedReportTypeId
      );
      const selectedOption = filteredReportTypesOptions.find(
        (option) => String(option.value) === String(reportTypeID)
      );
      const ReportType =
        selectedReport?.reportName || selectedOption?.label || "";

      if (!normalizedReportTypeId || !ReportType) {
        Swal.fire({
          icon: "error",
          title: "Invalid Report Selection",
          text: "Please re-select report type and try again.",
        });
        setSubmitting(false);
        return;
      }

      const reportStartDate =
        StartDate && StartDate !== "" ? StartDate : null;
      const reportEndDate = EndDate && EndDate !== "" ? EndDate : null;

      const mutationData = {
        ReportTypeId: normalizedReportTypeId,
        ReportType,
        BranchId: branchID,
        StartDate: reportStartDate,
        EndDate: reportEndDate,
        UserId: userID === "" || userID == null ? null : Number(userID),
      };

      if (normalizedReportTypeId === 1019 && userID) {
        mutationData.UserId = userID === "" ? null : Number(userID);
      }

      // Open a new tab immediately (avoids popup blocker)
      const newTab = window.open("", "_blank");
      newTab.document.write(`
        <html>
          <head>
            <title>Loading...</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background-color: #f0f0f0;
              }
              .loading { font-size: 24px; color: #333; }
            </style>
          </head>
          <body>
            <div class="loading">Loading, please wait...</div>
          </body>
        </html>
      `);
      newTab.document.close();

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading
      );

      if (response?.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        Swal.fire({
          icon: "error",
          title: "Report Error",
          text: "Report service rejected the request. Please check report parameters and try again.",
        });
        console.error("Error:", response.statusText);
        newTab.close();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  return (
    <Card m="10px" sx={{ background: `${colors.primary[400]} !important` }}>
      <DynamicForm
        fields={fields}
        onSubmit={handleSubmit}
        title="Report Browser"
        subtitle={
          isBranchLocked
            ? `Please select the report parameters — Branch: ${lockedBranchName}`
            : "Please select the report parameters"
        }
        initialValues={initialValues}
        onFieldChange={onFieldChange}
      />
    </Card>
  );
};

export default Filter;